/**
 * Lab Detail Activity matrix helpers (Agent `memberDetailLab` parity).
 * Figures from Strix analytics via CM-API member detail / member-pnl.
 */
import { fromMinorUnits } from '@/features/hierarchy/hierarchy'

export type ActivityPeriod =
  | 'today'
  | 'days3'
  | 'days7'
  | 'days30'
  | 'lifetime'
  | 'custom'

export const ACTIVITY_PERIODS: { id: ActivityPeriod; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'days3', label: '3 Days' },
  { id: 'days7', label: '7 Days' },
  { id: 'days30', label: '30 Days' },
  { id: 'lifetime', label: 'Lifetime' },
  { id: 'custom', label: 'Custom Dates' },
]

export type ActivityMetric = 'win' | 'betfairComm' | 'commission' | 'pnl' | 'turnover'

export const ACTIVITY_METRICS: { id: ActivityMetric; label: string }[] = [
  { id: 'win', label: 'Win' },
  { id: 'betfairComm', label: 'Betfair Comm' },
  { id: 'commission', label: 'Commission' },
  { id: 'pnl', label: 'P&L' },
  { id: 'turnover', label: 'Turnover' },
]

/** Activity Select Games / Sub Games — these labels are removed from the options. */
const ACTIVITY_REMOVED_FILTER_NAMES = new Set([
  'uof',
  'racing',
  'transaction',
  'evenbet',
  'zerowin',
])

function activityFilterNameKey(name: string): string {
  return name.trim().toLowerCase().replace(/[\s_-]+/g, '')
}

/** True when a game/sub-game label must not appear in Activity filter selects. */
export function isActivityRemovedFilterName(name: string): boolean {
  const key = activityFilterNameKey(name)
  if (ACTIVITY_REMOVED_FILTER_NAMES.has(key)) return true
  // Live catalog sometimes uses "UOF Markets" etc.
  return key.startsWith('uof')
}

export type ActivityPeriodValues = Record<ActivityPeriod, number>

export type MemberActivityMatrix = {
  memberId: string
  memberScope: 'agent' | 'all-members'
  values: Record<ActivityMetric, ActivityPeriodValues>
}

export type ActivityPeriodFigures = {
  win: number
  betfairComm: number
  commission: number
  pnl: number
  turnover: number
}

function fromMinorUnitsSigned(minor: string | null | undefined): number {
  if (minor == null || minor === '') return 0
  const n = Number(minor)
  return Number.isFinite(n) ? n / 100 : 0
}

export function emptyPeriodValues(): ActivityPeriodValues {
  return {
    today: 0,
    days3: 0,
    days7: 0,
    days30: 0,
    lifetime: 0,
    custom: 0,
  }
}

export function emptyActivityMatrix(
  memberId: string,
  memberScope: 'agent' | 'all-members' = 'agent',
): MemberActivityMatrix {
  return {
    memberId,
    memberScope,
    values: {
      win: emptyPeriodValues(),
      betfairComm: emptyPeriodValues(),
      commission: emptyPeriodValues(),
      pnl: emptyPeriodValues(),
      turnover: emptyPeriodValues(),
    },
  }
}

export function emptyPeriodFigures(): ActivityPeriodFigures {
  return { win: 0, betfairComm: 0, commission: 0, pnl: 0, turnover: 0 }
}

export function figuresFromMemberPeriod(period?: {
  turnoverMinor?: string
  memberNetMinor?: string
  bookNetMinor?: string
}): ActivityPeriodFigures {
  const pnl =
    period?.memberNetMinor !== undefined && period.memberNetMinor !== ''
      ? fromMinorUnitsSigned(period.memberNetMinor)
      : period?.bookNetMinor !== undefined && period.bookNetMinor !== ''
        ? -fromMinorUnitsSigned(period.bookNetMinor)
        : 0
  return {
    win: Math.max(0, pnl),
    betfairComm: 0,
    commission: 0,
    pnl,
    turnover: fromMinorUnits(period?.turnoverMinor),
  }
}

export function setPeriodFigures(
  base: MemberActivityMatrix,
  period: ActivityPeriod,
  figures: ActivityPeriodFigures,
): MemberActivityMatrix {
  const next = emptyActivityMatrix(base.memberId, base.memberScope)
  next.values = {
    win: { ...base.values.win, [period]: figures.win },
    betfairComm: { ...base.values.betfairComm, [period]: figures.betfairComm },
    commission: { ...base.values.commission, [period]: figures.commission },
    pnl: { ...base.values.pnl, [period]: figures.pnl },
    turnover: { ...base.values.turnover, [period]: figures.turnover },
  }
  return next
}

export function isoDayLocal(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function addDaysLocal(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(y!, m! - 1, d!)
  dt.setDate(dt.getDate() + delta)
  return isoDayLocal(dt)
}

export function activityPeriodRanges(
  customFrom: string,
  customTo: string,
  today: string = isoDayLocal(),
): Record<ActivityPeriod, { from: string; to: string }> {
  const from = customFrom.slice(0, 10)
  const to = customTo.slice(0, 10)
  return {
    today: { from: today, to: today },
    days3: { from: addDaysLocal(today, -2), to: today },
    days7: { from: addDaysLocal(today, -6), to: today },
    days30: { from: addDaysLocal(today, -29), to: today },
    lifetime: { from: '2015-01-01', to: today },
    custom: { from: from <= to ? from : to, to: from <= to ? to : from },
  }
}
